// 기능이 사용되는 페이지에 "본 제품은 한글과컴퓨터의 HWP 문서 파일(.hwp,.hwpx) 공개 문서를 참고하여 개발하였습니다." 노출
import {
  PAPER_TABS,
  PAPER_CONFIG,
  AUDIO_PAPER_TYPE,
} from '../../../data/common.js';
import { strParse } from '../../../lib/util.js';
import pako from 'pako';
import { base_hwp, bin_background } from './cvBaseData.js';
import * as CFB from 'cfb';
import CRC32 from 'crc-32';

const MAX_SINGLE_WIDTH = 54000;
const MAX_DOUBLE_WIDTH = 25500;
const NUMBER_WIDTH = 2500;
const OPTION_NUMBER_WIDTH = 1133;

const ALIGN_CENTER = 18;
const ALIGN_LEFT = 15;

function base64ToArrayBuffer(base64) {
  let cleanBase64 = base64.replace(/^data:.*;base64,/, '');
  const binaryString = atob(cleanBase64); // Base64 디코딩 (문자열 변환)
  const length = binaryString.length;
  const buffer = new ArrayBuffer(length);
  const view = new Uint8Array(buffer);

  for (let i = 0; i < length; i++) {
    view[i] = binaryString.charCodeAt(i); // 각 문자 코드를 8비트 값으로 저장
  }

  return buffer;
}

function mergeBuffers(buffers) {
  // 전체 합친 버퍼 크기 계산
  const totalLength = buffers.reduce(
    (sum, buffer) => sum + buffer.byteLength,
    0,
  );

  // Uint8Array로 새로운 버퍼 생성
  const mergedArray = new Uint8Array(totalLength);

  let offset = 0;
  buffers.forEach(buffer => {
    mergedArray.set(new Uint8Array(buffer), offset); // 데이터 복사
    offset += buffer.byteLength;
  });

  return mergedArray;
}

function compressBinary(input) {
  const compressed = pako.deflateRaw(input, { level: 9 }); // Zlib 압축
  return compressed;
}

function stringToUint8ArrayUTF16LE(str) {
  const buffer = new ArrayBuffer(str.length * 2); // UTF-16LE는 2바이트 단위
  const view = new DataView(buffer);

  for (let i = 0; i < str.length; i++) {
    view.setUint16(i * 2, str.charCodeAt(i), true); // Little Endian 저장
  }

  return new Uint8Array(buffer);
}

/**
 *
 * 여기에서 각 태그 별 데이터 병합
 *
 * tagId별 기능(16 부터 시작하나 사용하는 tagId만 기입)
 * DocInfo 영역
 * 17 : 각 정보 아이템 count
 * 18 : 포함되어 있는 binary data(image 등) 정보
 * 20 : 테두리 배경 프리셋
 * 21 : 글자 모양 프리셋
 * 25 : 문단 모양 프리셋
 *
 * Section 영역
 * 66 : 문단 헤더. 문단이 시작할 때 필요
 * 67 : 문단 텍스트
 * 68 : 문단 텍스트 모양 배열
 * 69 : 문단 레이아웃
 * 71 : 컨트롤 헤더
 * 72 : 문단 리스트 헤더
 * 75 : 쪽 테두리/배경
 * 77 : 표 개체
 *
 */
function assembleBinaryData(items) {
  let totalSize = 0;
  const buffers = [];

  for (const item of items) {
    const { tagId, headerLevel, data } = item;
    const dataSize = data.length;
    let level = 1;
    if (headerLevel !== undefined) {
      level = headerLevel;
    }

    // 4바이트 헤더 구성 (tagId, headerLevel=0, dataSize)
    const header = new Uint8Array(4);
    let tagBits = tagId
      .toString(2)
      .padStart(10, '0')
      .split('')
      .reverse()
      .join('');
    let headerBits = level
      .toString(2)
      .padStart(10, '0')
      .split('')
      .reverse()
      .join(''); // headerLevel 10비트 (기본값 0)
    let sizeBits = dataSize
      .toString(2)
      .padStart(12, '0')
      .split('')
      .reverse()
      .join('');

    // 32비트 헤더를 Uint8Array로 변환
    const headerBitsString = tagBits + headerBits + sizeBits;
    for (let i = 0; i < 4; i++) {
      header[i] = parseInt(
        headerBitsString
          .slice(i * 8, (i + 1) * 8)
          .split('')
          .reverse()
          .join(''),
        2,
      );
    }

    buffers.push(header);
    buffers.push(new Uint8Array(data));
    totalSize += 4 + dataSize;
  }

  // 전체 데이터를 합친 `ArrayBuffer` 반환
  let resultBuffer = new Uint8Array(totalSize);
  let offset = 0;
  for (const buffer of buffers) {
    resultBuffer.set(buffer, offset);
    offset += buffer.length;
  }

  return resultBuffer;
}

function byteToStringLittleEndian16(buffer) {
  const uint8Array = new Uint8Array(buffer);
  const length = uint8Array.length;

  if (length % 2 !== 0) {
    throw new Error('데이터 길이는 반드시 짝수여야 합니다!'); // 2바이트 단위 변환을 위해 필요
  }

  let result = '';

  for (let i = 0; i < length; i += 2) {
    // 2바이트(16비트) 단위로 순서를 뒤집음
    const charCode = (uint8Array[i + 1] << 8) | uint8Array[i]; // 리틀 엔디언 변환
    if (charCode === 0) {
      break;
    }
    result += String.fromCharCode(charCode);
  }

  return result;
}

function read_file_data(hexArray, start, end) {
  return hexArray.slice(start, end);
}
function byteToIntLitEndian(fileStream) {
  if (!fileStream) return 0; // null 또는 undefined 체크

  // 문자열을 뒤집고 16진수로 변환
  const reversedHex = [...fileStream]
    .map(byte => byte.toString(16).padStart(2, '0'))
    .reverse()
    .join('');
  return parseInt(reversedHex, 16);
}

function intToByteLitEndian(value, byteSize) {
  if (byteSize < 1 || byteSize > 8) {
    throw new Error('byteSize는 1~8 사이여야 합니다.');
  }

  const byteArray = new Uint8Array(byteSize);

  for (let i = 0; i < byteSize; i++) {
    byteArray[i] = (value >> (8 * i)) & 0xff; // Little Endian 순서 저장
  }

  return byteArray;
}

function getBaseHWP(base_hwp) {
  const data = base64ToArrayBuffer(base_hwp);
  const hexArray = new Uint8Array(data);
  const result = {};
  result['header'] = {};

  // 파일을 바이너리(Buffer)로 읽기
  result['header']['raw'] = hexArray.slice(0, 76);
  const filesize = hexArray.length;
  const sector_shift =
    2 ** byteToIntLitEndian(read_file_data(hexArray, 30, 32));
  const mini_sector_shift =
    2 ** byteToIntLitEndian(read_file_data(hexArray, 32, 34));
  const fat_sector = byteToIntLitEndian(read_file_data(hexArray, 44, 48));
  const sec_id = byteToIntLitEndian(read_file_data(hexArray, 48, 52));
  const mini_stream_cutoff_size = byteToIntLitEndian(
    read_file_data(hexArray, 56, 60),
  );
  const mini_fat_location = byteToIntLitEndian(
    read_file_data(hexArray, 60, 64),
  );
  const mini_fat_count = byteToIntLitEndian(read_file_data(hexArray, 64, 68)); // mini fat sector count
  result['header']['header_cisid'] = parseInt(hexArray.slice(8, 16));
  result['header']['header_blank'] = parseInt(hexArray.slice(16, 24));
  result['header']['version'] = parseInt(hexArray.slice(24, 28));
  result['header']['byte_order_identifier'] = parseInt(hexArray.slice(28, 30));
  result['header']['sector_shift'] = sector_shift;
  result['header']['mini_sector_shift'] = mini_sector_shift;
  result['header']['fat_sector_count'] = fat_sector;
  result['header']['root_entry_id'] = sec_id;
  result['header']['mini_stream_cutoff_size'] = mini_stream_cutoff_size;
  result['header']['mini_fat_location'] = mini_fat_location;
  result['header']['mini_fat_count'] = mini_fat_count;

  //console.log(result["header"]);

  const fat_sector_index = [];
  for (let i = 0; i < fat_sector; i++) {
    fat_sector_index.push(
      byteToIntLitEndian(read_file_data(hexArray, 76 + i * 4, 76 + i * 4 + 4)),
    );
  }

  const sectors = [];
  let offset = 512;
  while (offset < filesize) {
    const sector_item = read_file_data(hexArray, offset, offset + sector_shift);
    sectors.push(sector_item);
    offset += sector_shift;
  }

  const fat_sectors = [];
  for (let i = 0; i < fat_sector_index.length; i++) {
    let fat_offset = 0;
    while (fat_offset < sector_shift && fat_sectors.length <= sectors.length) {
      fat_sectors.push(
        byteToIntLitEndian(
          sectors[fat_sector_index[i]].slice(fat_offset, fat_offset + 3),
        ),
      );
      fat_offset += 4;
    }
  }

  const mini_fat_sectors = [];
  let mini_fat_sector_index = mini_fat_location;
  while (mini_fat_sector_index < fat_sectors.length) {
    let mini_fat_offset = 0;
    while (mini_fat_offset < sector_shift) {
      mini_fat_sectors.push(
        byteToIntLitEndian(
          sectors[mini_fat_location].slice(
            mini_fat_offset,
            mini_fat_offset + 3,
          ),
        ),
      );
      mini_fat_offset += 4;
    }
    mini_fat_sector_index = fat_sectors[mini_fat_sector_index];
  }

  const root_entry_sectors = [];
  let root_entry_sector_index = sec_id;
  while (root_entry_sector_index < fat_sectors.length) {
    root_entry_sectors.push(sectors[root_entry_sector_index]);
    root_entry_sector_index = fat_sectors[root_entry_sector_index];
  }

  let root_entry_data = mergeBuffers(root_entry_sectors);

  result['root_entry'] = [];

  let root_entry_data_offset = 0;
  const sectors_mini = [];
  while (root_entry_data_offset < root_entry_data.length) {
    let root_entry_item = {};

    root_entry_item['raw'] = root_entry_data.slice(
      root_entry_data_offset,
      root_entry_data_offset + 128,
    );

    root_entry_item['name'] = byteToStringLittleEndian16(
      root_entry_data.slice(
        root_entry_data_offset,
        root_entry_data_offset + 64,
      ),
    ); //64
    root_entry_data_offset += 64;

    root_entry_item['name_length'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 2),
    ); //66
    root_entry_data_offset += 2;

    root_entry_item['object_type'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 1),
    ); //67
    root_entry_data_offset += 1;

    root_entry_item['color_flag'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 1),
    ); //68
    root_entry_data_offset += 1;

    root_entry_item['left_sibling_id'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 4),
    ); //72
    root_entry_data_offset += 4;

    root_entry_item['right_sibling_id'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 4),
    ); //76
    root_entry_data_offset += 4;

    root_entry_item['child_id'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 4),
    ); // 80
    root_entry_data_offset += 4;

    root_entry_item['cls_id'] = byteToIntLitEndian(
      root_entry_data.slice(
        root_entry_data_offset,
        root_entry_data_offset + 16,
      ),
    ); // 96
    root_entry_data_offset += 16;

    root_entry_item['state_bits'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 4),
    ); // 100
    root_entry_data_offset += 4;

    root_entry_item['creation_time'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 8),
    ); // 108
    root_entry_data_offset += 8;

    root_entry_item['modified_time'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 8),
    ); // 116
    root_entry_data_offset += 8;

    root_entry_item['starting_selector_location'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 4),
    ); // 120
    root_entry_data_offset += 4;

    root_entry_item['stream_size'] = byteToIntLitEndian(
      root_entry_data.slice(root_entry_data_offset, root_entry_data_offset + 8),
    ); // 128
    root_entry_data_offset += 8;

    if (root_entry_item['stream_size'] >= mini_stream_cutoff_size) {
      root_entry_item['type'] = 'fat';
    } else {
      root_entry_item['type'] = 'mini_fat';
    }

    result['root_entry'].push(root_entry_item);

    if (root_entry_item['name'] === 'Root Entry') {
      let mini_fat_sector_index = root_entry_item['starting_selector_location'];
      while (sectors.length > mini_fat_sector_index) {
        let s = sectors[mini_fat_sector_index];
        let mini_sector_offset = 0;
        while (mini_sector_offset < sector_shift) {
          sectors_mini.push(
            s.slice(mini_sector_offset, mini_sector_offset + mini_sector_shift),
          );
          mini_sector_offset += mini_sector_shift;
        }
        mini_fat_sector_index = fat_sectors[mini_fat_sector_index];
      }
    }
  }
  for (let i = 0; i < result['root_entry'].length; i++) {
    if (result['root_entry'][i]['object_type'] === 2) {
      let data = [];
      let offset = result['root_entry'][i]['starting_selector_location'];
      if (result['root_entry'][i]['type'] === 'fat') {
        while (offset < sectors.length) {
          data.push(sectors[offset]);
          offset = fat_sectors[offset];
        }
      } else if (result['root_entry'][i]['type'] === 'mini_fat') {
        while (offset < sectors_mini.length) {
          data.push(sectors_mini[offset]);
          offset = mini_fat_sectors[offset];
        }
      }
      let merge_data = mergeBuffers(data);
      result['root_entry'][i]['document_raw'] = merge_data.slice(
        0,
        result['root_entry'][i]['stream_size'],
      );
      //console.log(result["root_entry"][i]["name"]+":"+result["root_entry"][i]["stream_size"]);
    }
  }

  return result;
}

// 문제 제목 생성
const createSubject = (subjectText, fontSize) => {
  let subjectText_byte = stringToUint8ArrayUTF16LE('※ ' + subjectText);
  subjectText_byte = new Uint8Array([...subjectText_byte, 13, 0]);
  const items = [];
  const char_count = subjectText_byte.length / 2;
  items.push({
    tagId: 66,
    headerLevel: 0,
    data: [
      1, 0, 0, 0, 0, 0, 0, 0, 13, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  items.push({ tagId: 68, headerLevel: 1, data: [0, 0, 0, 0, 10, 0, 0, 0] });
  items.push({
    tagId: 69,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 148, 4, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
      0, 0, 0, 0, 0, 0, 108, 102, 0, 0, 0, 0, 6, 0,
    ],
  });
  items.push({
    tagId: 66,
    headerLevel: 0,
    data: [
      ...intToByteLitEndian(char_count, 4),
      0,
      0,
      0,
      0,
      13,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  });
  items.push({ tagId: 67, headerLevel: 1, data: subjectText_byte }); // para_text\ ※ 다음을 듣고 알맞은 영단어를 쓰세요. \13
  items.push({
    tagId: 68,
    headerLevel: 1,
    data: [0, 0, 0, 0, fontSize[1], 0, 0, 0],
  });
  items.push({
    tagId: 69,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 148, 4, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
      0, 0, 0, 0, 0, 0, 108, 102, 0, 0, 0, 0, 6, 0,
    ],
  });
  items.push({
    tagId: 66,
    headerLevel: 0,
    data: [
      1, 0, 0, 0, 0, 0, 0, 0, 13, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  items.push({ tagId: 68, headerLevel: 1, data: [0, 0, 0, 0, 2, 0, 0, 0] });
  items.push({
    tagId: 69,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 148, 4, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
      0, 0, 0, 0, 0, 0, 108, 102, 0, 0, 0, 0, 6, 0,
    ],
  });
  return items;
};

// A타입 텍스트 항목 생성
const createAtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(text.question);
  let answer_byte = stringToUint8ArrayUTF16LE(
    paperTab === PAPER_TABS.TEST_PAPER ? '' : text.answer,
  );
  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);
  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(2, 2);
  const table_col = intToByteLitEndian(3, 2);

  // '시험지', '정답표시 시험지'
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        3,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH) / 2, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        2,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH) / 2, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        5,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        ALIGN_CENTER,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        141,
        0,
        1,
        0,
        8,
        7,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// B타입 텍스트 항목 생성
const createBtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(text.question);
  let answer_byte = stringToUint8ArrayUTF16LE(
    `${
      vocaFirstWordShow
        ? paperTab === PAPER_TABS.TEST_PAPER
          ? text.answer[0]
          : text.answer
        : paperTab === PAPER_TABS.TEST_PAPER
          ? ''
          : text.answer
    }`,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);
  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(2, 2);
  const table_col = intToByteLitEndian(3, 2);

  // '시험지', '정답표시 시험지'
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        3,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        141,
        0,
        1,
        0,
        8,
        7,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH) / 2, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        64,
        0,
        0,
        0,
        2,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH) / 2, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        5,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        vocaFirstWordShow ? ALIGN_LEFT : ALIGN_CENTER,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// C타입 텍스트 항목 생성
const createCtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(
    `${[
      ...text.questions.map(subText => {
        return subText.questionTranslation;
      }),
    ].join('')}`,
  );
  let content = [
    ...text.questions.map(subText => {
      return paperTab === PAPER_TABS.TEST_PAPER
        ? [
            ...(subText.questionSentence ?? '')
              .split(/(\{[^{}]*\}|[^\s{}]+)/) // {} 내부와 외부를 구분하면서 split
              .filter(Boolean) // 빈 문자열 제거
              .flatMap(word => {
                if (word.includes('{') && word.includes('}')) {
                  // {}로 둘러싸인 경우 처리
                  const editWord = word
                    .replace(/[{}]/g, '') // {} 제거
                    .split('^');
                  const words = editWord.map((_, idx) => {
                    return idx === editWord?.length - 1
                      ? vocaFirstWordShow
                        ? `${editWord[idx][0]}__________________`
                        : '___________________'
                      : vocaFirstWordShow
                        ? `${editWord[idx][0]}__________________ `
                        : '___________________ ';
                  });
                  return words.join('');
                } else {
                  // 일반 단어 처리
                  return `${word + ''}`;
                }
              }),
          ].join('')
        : [
            ...(subText.questionSentence ?? '')
              .split(/(\{[^{}]*\}|[^\s{}]+)/) // {} 내부와 외부를 구분하면서 split
              .filter(Boolean) // 빈 문자열 제거
              .map(word => {
                if (word.includes('{') && word.includes('}')) {
                  // {} 내부 텍스트 처리
                  const cleanedText = word
                    .replace(/[{}]/g, '') // {} 제거
                    .replace(/[!@#$%&*()?><":;.,]/g, '') // 특수문자 제거
                    .split('^');
                  // ^로 분리 후 각 단어를 밑줄 처리
                  return cleanedText
                    .flatMap((cleanWord, idx) =>
                      [
                        `<${cleanWord}>`,
                        idx === cleanedText?.length - 1 ? `` : ` `,
                      ].join(''),
                    )
                    .join('');
                } else {
                  // 일반 단어 처리
                  return `${word + ''}`;
                }
              }),
          ].join('');
    }),
  ].join('');
  let offset = 0;
  let answer_start_index_arr = [];
  let answer_end_index_arr = [];
  for (let i = 0; i < content.length; i++) {
    if (content[i] === '<') {
      answer_start_index_arr.push(i - offset);
      offset += 1;
    } else if (content[i] === '>') {
      answer_end_index_arr.push(i - offset);
      offset += 1;
    }
  }
  content = content.replace(/[<>]/g, '');
  let answer_byte = stringToUint8ArrayUTF16LE(
    paperTab === PAPER_TABS.ANSWER ? text.word : content,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);
  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(3, 2);
  const table_col = intToByteLitEndian(2, 2);

  // '시험지', '정답표시 시험지'
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        2,
        0,
        2,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        141,
        0,
        1,
        0,
        8,
        7,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 8, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 212, 6, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    let char_shape_count =
      answer_start_index_arr.length === answer_end_index_arr.length &&
      answer_start_index_arr.length > 0
        ? answer_start_index_arr[0] === 0
          ? answer_start_index_arr.length * 2
          : answer_start_index_arr.length * 2 + 1
        : 1;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        ALIGN_LEFT,
        0,
        0,
        0,
        char_shape_count,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    if (
      answer_start_index_arr.length === answer_end_index_arr.length &&
      answer_start_index_arr.length > 0
    ) {
      let index_byte = [];
      if (answer_start_index_arr[0] !== 0) {
        index_byte = [0, 0, 0, 0, fontSize[0], 0, 0, 0];
      }
      for (let i = 0; i < answer_start_index_arr.length; i++) {
        index_byte = [
          ...index_byte,
          ...intToByteLitEndian(answer_start_index_arr[i], 4),
          fontSize[2],
          0,
          0,
          0,
          ...intToByteLitEndian(answer_end_index_arr[i], 4),
          fontSize[0],
          0,
          0,
          0,
        ];
      }
      console.log('c_index');
      console.log(index_byte);
      items.push({ tagId: 68, headerLevel: 3, data: index_byte });
    } else {
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
    }
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        2,
        0,
        2,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// D타입 텍스트 항목 생성
const createDtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(strParse(text.question));

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(2 + text.synonyms.length * 3, 2);
  const table_col = intToByteLitEndian(4, 2);

  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    let row_size = [];
    for (let j = 0; j < text.synonyms.length; j++) {
      row_size.push(4);
      row_size.push(0);
      row_size.push(1);
      row_size.push(0);
      row_size.push(1);
      row_size.push(0);
    }
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        50,
        0,
        2,
        0,
        1,
        0,
        ...row_size,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        3,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        4,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });

    for (let i = 0; i < text.synonyms.length; i++) {
      let word = text.synonyms[i];
      let type_byte = stringToUint8ArrayUTF16LE(`(${word.type})`);
      type_byte = new Uint8Array([...type_byte, 13, 0]);
      const type_char_cnt = intToByteLitEndian(type_byte.length / 2, 4);

      let word_meaning_byte = stringToUint8ArrayUTF16LE(word.meaning);
      word_meaning_byte = new Uint8Array([...word_meaning_byte, 13, 0]);
      const word_meaning_char_cnt = intToByteLitEndian(
        word_meaning_byte.length / 2,
        4,
      );

      const row_index = intToByteLitEndian(i * 3 + 2, 2);
      const row_align_index = intToByteLitEndian(i * 3 + 3, 2);
      const row_blank_index = intToByteLitEndian(i * 3 + 4, 2);
      let answer_byte = stringToUint8ArrayUTF16LE(
        `${
          vocaFirstWordShow
            ? paperTab === PAPER_TABS.TEST_PAPER
              ? word.answer[0]
              : word.answer
            : paperTab === PAPER_TABS.TEST_PAPER
              ? ''
              : word.answer // 정답 필드
        }`,
      );
      answer_byte = new Uint8Array([...answer_byte, 13, 0]);
      const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          ...row_index,
          1,
          0,
          1,
          0,
          ...number_width,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          3, 0, 0, 128, 0, 0, 0, 0, 5, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: [32, 0, 45, 0, 13, 0] });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          ...row_index,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian((NUMBER_WIDTH * 4) / 3, 4),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      type_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...type_char_cnt,
          0,
          0,
          0,
          0,
          17,
          0,
          2,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: type_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 116,
          255, 255, 255, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          64,
          0,
          0,
          0,
          2,
          0,
          ...row_index,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(
            (table_width_ori - (NUMBER_WIDTH * 7) / 3) / 2,
            4,
          ),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          5,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      answer_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...answer_char_cnt,
          0,
          0,
          0,
          0,
          vocaFirstWordShow ? ALIGN_LEFT : ALIGN_CENTER,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      if (answer_byte.length / 2 > 1) {
        items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
      }
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 116,
          255, 255, 255, 0, 0, 0, 0, 196, 42, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          3,
          0,
          ...row_index,
          1,
          0,
          2,
          0,
          ...intToByteLitEndian(
            (table_width_ori - (NUMBER_WIDTH * 7) / 3) / 2,
            4,
          ),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      word_meaning_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...word_meaning_char_cnt,
          0,
          0,
          0,
          0,
          9,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: word_meaning_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 144, 42, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          ...row_align_index,
          3,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 5, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
          0,
        ],
      });
      items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
          0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          ...row_blank_index,
          4,
          0,
          1,
          0,
          ...table_width,
          ...intToByteLitEndian(lineSpacing, 4),
          0,
          0,
          0,
          0,
          141,
          0,
          141,
          0,
          1,
          0,
          156,
          99,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0,
          0, 0,
        ],
      });
      items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
          0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
        ],
      });
    }
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    let answer_byte = stringToUint8ArrayUTF16LE(
      `${[
        ...text.synonyms.map((word, indx) => {
          const length = text?.synonyms?.length;
          return indx !== length - 1 ? `${word.answer}, ` : word.answer;
        }),
      ].join('')}`,
    );
    answer_byte = new Uint8Array([...answer_byte, 13, 0]);
    const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// E타입 텍스트 항목 생성
const createEtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(strParse(text.question));

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);
  const blank_width = intToByteLitEndian(850, 4);

  const table_row = intToByteLitEndian(2 + text.deriveds.length * 3, 2);
  const table_col = intToByteLitEndian(4, 2);

  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    let row_size = [];
    for (let j = 0; j < text.deriveds.length; j++) {
      row_size.push(4);
      row_size.push(0);
      row_size.push(1);
      row_size.push(0);
      row_size.push(1);
      row_size.push(0);
    }
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        50,
        0,
        2,
        0,
        1,
        0,
        ...row_size,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        3,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        4,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });

    for (let i = 0; i < text.deriveds.length; i++) {
      let word = text.deriveds[i];

      let word_meaning_byte = stringToUint8ArrayUTF16LE(word.question);
      word_meaning_byte = new Uint8Array([...word_meaning_byte, 13, 0]);
      const word_meaning_char_cnt = intToByteLitEndian(
        word_meaning_byte.length / 2,
        4,
      );

      const row_index = intToByteLitEndian(i * 3 + 2, 2);
      const row_align_index = intToByteLitEndian(i * 3 + 3, 2);
      const row_blank_index = intToByteLitEndian(i * 3 + 4, 2);
      let answer_byte = stringToUint8ArrayUTF16LE(
        `${
          vocaFirstWordShow
            ? paperTab === PAPER_TABS.TEST_PAPER
              ? word.answer[0]
              : word.answer
            : paperTab === PAPER_TABS.TEST_PAPER
              ? ''
              : word.answer
        }`,
      );
      answer_byte = new Uint8Array([...answer_byte, 13, 0]);
      const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          ...row_index,
          1,
          0,
          1,
          0,
          ...number_width,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          3, 0, 0, 128, 0, 0, 0, 0, 5, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: [32, 0, 45, 0, 13, 0] });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          ...row_index,
          1,
          0,
          1,
          0,
          ...blank_width,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 17, 0, 2, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0,
          0, 0,
        ],
      });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 116,
          255, 255, 255, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          64,
          0,
          0,
          0,
          2,
          0,
          ...row_index,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH - 850) / 2, 4),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          5,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      answer_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...answer_char_cnt,
          0,
          0,
          0,
          0,
          vocaFirstWordShow ? ALIGN_LEFT : ALIGN_CENTER,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      if (answer_byte.length / 2 > 1) {
        items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
      }
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 116,
          255, 255, 255, 0, 0, 0, 0, 196, 42, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          3,
          0,
          ...row_index,
          1,
          0,
          2,
          0,
          ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH - 850) / 2, 4),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      word_meaning_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...word_meaning_char_cnt,
          0,
          0,
          0,
          0,
          9,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: word_meaning_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 144, 42, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          ...row_align_index,
          3,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 5, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
          0,
        ],
      });
      items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          ...row_blank_index,
          4,
          0,
          1,
          0,
          ...table_width,
          ...intToByteLitEndian(lineSpacing, 4),
          0,
          0,
          0,
          0,
          141,
          0,
          141,
          0,
          1,
          0,
          156,
          99,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0,
          0, 0,
        ],
      });
      items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
          0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
        ],
      });
    }
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    let answer_byte = stringToUint8ArrayUTF16LE(
      `${[
        ...text.deriveds.map((word, indx) => {
          const length = text?.deriveds?.length;
          return indx !== length - 1 ? `${word.answer}, ` : word.answer;
        }),
      ].join('')}`,
    );
    answer_byte = new Uint8Array([...answer_byte, 13, 0]);
    const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// F타입 텍스트 항목 생성
const createFtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(
    `${
      text.audioQuestionType === AUDIO_PAPER_TYPE.KOR
        ? paperTab === PAPER_TABS.TEST_PAPER
          ? ''
          : strParse(text.meanings)
        : vocaFirstWordShow
          ? paperTab === PAPER_TABS.TEST_PAPER
            ? text.answer[0]
            : text.answer
          : paperTab === PAPER_TABS.TEST_PAPER
            ? ''
            : text.answer
    }`,
  );
  let answer_byte = stringToUint8ArrayUTF16LE(
    paperTab === PAPER_TABS.ANSWER
      ? `${text.answer} ${strParse(text.meanings)}`
      : `${
          text.audioQuestionType === AUDIO_PAPER_TYPE.ENG_KOR
            ? paperTab === PAPER_TABS.TEST_PAPER
              ? ''
              : strParse(text.meanings)
            : ''
        }`,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(2, 2);
  const table_col = intToByteLitEndian(4, 2);

  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        4,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH * 2) / 2, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        5,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        vocaFirstWordShow ? ALIGN_LEFT : ALIGN_CENTER,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (question_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        2,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 16, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 196, 42, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        64,
        0,
        0,
        0,
        3,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH * 2) / 2, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        text.audioQuestionType === AUDIO_PAPER_TYPE.ENG_KOR ? 5 : 1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        vocaFirstWordShow ? ALIGN_LEFT : ALIGN_CENTER,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 196, 42, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        4,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        141,
        0,
        141,
        0,
        1,
        0,
        156,
        99,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// G타입 텍스트 항목 생성
const createGtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(`${strParse(text.question)}`);
  let answer_byte = stringToUint8ArrayUTF16LE(
    paperTab === PAPER_TABS.ANSWER
      ? `${text.answer}`
      : `${
          vocaFirstWordShow
            ? paperTab === PAPER_TABS.TEST_PAPER
              ? text.answer[0]
              : text.answer
            : paperTab === PAPER_TABS.TEST_PAPER
              ? ''
              : text.answer
        }`,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(4, 2);
  const table_col = intToByteLitEndian(3, 2);

  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        2,
        0,
        1,
        0,
        3,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        2,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 14, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 100,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        2,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        64,
        0,
        0,
        0,
        1,
        0,
        2,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH) / 2, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        5,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        vocaFirstWordShow ? ALIGN_LEFT : ALIGN_CENTER,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 72, 42, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        2,
        0,
        2,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian((table_width_ori - NUMBER_WIDTH) / 2, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        3,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// H타입 텍스트 항목 생성
const createHtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(`${strParse(text.question)}`);
  let answer_byte = stringToUint8ArrayUTF16LE(
    `${String.fromCharCode(9312 + text?.answer - 1)}`,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(6, 2);
  const table_col = intToByteLitEndian(3, 2);

  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        2,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        2,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    for (let i = 0; i < text.options.length; i++) {
      const option_number = `${String.fromCharCode(9312 + i)}`;
      const option = text.options[i];
      let option_byte = stringToUint8ArrayUTF16LE(option);
      let option_number_byte = stringToUint8ArrayUTF16LE(option_number);
      option_byte = [...option_byte, 13, 0];
      option_number_byte = [...option_number_byte, 13, 0];
      const option_char_cnt = intToByteLitEndian(option_byte.length / 2, 4);
      const option_number_char_cnt = intToByteLitEndian(
        option_number_byte.length / 2,
        4,
      );

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...number_width,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0,
          0, 0,
        ],
      });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(OPTION_NUMBER_WIDTH, 4),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_number_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_number_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: option_number_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          2,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(
            table_width_ori - NUMBER_WIDTH - OPTION_NUMBER_WIDTH,
          ),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      if (option_byte.length / 2 > 1) {
        items.push({ tagId: 67, headerLevel: 3, data: option_byte });
      }
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [
          0,
          0,
          0,
          0,
          paperTab === PAPER_TABS.ANSWER_TEST_PAPER && text.answer === i + 1
            ? fontSize[2]
            : fontSize[0],
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });
    }

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        5,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// I타입 텍스트 항목 생성
const createItypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(`${strParse(text.question)}`);
  let answer_byte = stringToUint8ArrayUTF16LE(
    `${String.fromCharCode(9312 + text?.answer - 1)}`,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(6, 2);
  const table_col = intToByteLitEndian(3, 2);

  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        2,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        2,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    for (let i = 0; i < text.options.length; i++) {
      const option_number = `${String.fromCharCode(9312 + i)}`;
      const option = text.options[i];
      let option_byte = stringToUint8ArrayUTF16LE(option);
      let option_number_byte = stringToUint8ArrayUTF16LE(option_number);
      option_byte = [...option_byte, 13, 0];
      option_number_byte = [...option_number_byte, 13, 0];
      const option_char_cnt = intToByteLitEndian(option_byte.length / 2, 4);
      const option_number_char_cnt = intToByteLitEndian(
        option_number_byte.length / 2,
        4,
      );

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...number_width,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0,
          0, 0,
        ],
      });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(OPTION_NUMBER_WIDTH, 4),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_number_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_number_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: option_number_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          2,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(
            table_width_ori - NUMBER_WIDTH - OPTION_NUMBER_WIDTH,
          ),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      if (option_byte.length / 2 > 1) {
        items.push({ tagId: 67, headerLevel: 3, data: option_byte });
      }
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [
          0,
          0,
          0,
          0,
          paperTab === PAPER_TABS.ANSWER_TEST_PAPER && text.answer === i + 1
            ? fontSize[2]
            : fontSize[0],
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });
    }

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        5,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// J타입 텍스트 항목 생성
const createJtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  examKorShow,
  columCount,
) => {
  let answerTextIndex = null; // 영문 정답단어 식별용
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_text = `${[
    ...text.sentence.split(' ').map((text, tIndex) => {
      let isAnswerText = false;
      if (text.includes('{') && text.includes('}')) {
        isAnswerText = true;
        answerTextIndex = tIndex;
      }
      let resultText = '';
      if (isAnswerText) {
        resultText =
          answerTextIndex !== null
            ? `<${text?.replace(/[{}]/g, '')?.replace(/\^/g, ' ')}>`
            : `<${text?.replace(/[{}]/g, '')?.replace(/\^/g, ' ')} >`;
      } else {
        resultText =
          answerTextIndex !== null && tIndex === answerTextIndex + 1
            ? ` ${text} `
            : `${text} `;
      }
      return resultText;
    }),
  ].join('')}`;
  let answer_start_index = 0;
  let answer_end_index = 0;
  for (let i = 0; i < question_text.length; i++) {
    if (question_text[i] === '<') {
      answer_start_index = i;
    } else if (question_text[i] === '>') {
      answer_end_index = i - 1;
    }
  }

  console.log(answer_start_index + '/' + answer_end_index);
  question_text = question_text.replace(/[<>]/g, '');
  let question_byte = stringToUint8ArrayUTF16LE(question_text);
  let answer_byte = stringToUint8ArrayUTF16LE(
    `${String.fromCharCode(9312 + text?.answer - 1)}`,
  );
  let translation_byte = stringToUint8ArrayUTF16LE(
    `${!examKorShow ? '' : `(${text?.translation ?? ''})`}`,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);
  translation_byte = new Uint8Array([...translation_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);
  const translation_char_cnt = intToByteLitEndian(
    translation_byte.length / 2,
    4,
  );

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(7, 2);
  const table_col = intToByteLitEndian(3, 2);
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        2,
        0,
        2,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        2,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        answer_start_index < answer_end_index && answer_start_index >= 0
          ? 3
          : 1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    if (answer_start_index < answer_end_index && answer_start_index >= 0) {
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [
          0,
          0,
          0,
          0,
          fontSize[0],
          0,
          0,
          0,
          ...intToByteLitEndian(answer_start_index, 4),
          fontSize[2],
          0,
          0,
          0,
          ...intToByteLitEndian(answer_end_index, 4),
          fontSize[0],
          0,
          0,
          0,
        ],
      });
    } else {
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
    }
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 83, 2, 0, 0, 212, 0, 0,
        0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        2,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    translation_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...translation_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (translation_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: translation_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, examKorShow ? fontSize[0] : 21, 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 83, 2, 0, 0, 212, 0, 0,
        0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    for (let i = 0; i < text.options.length; i++) {
      const option_number = `${String.fromCharCode(9312 + i)}`;
      const option = text.options[i];
      let option_byte = stringToUint8ArrayUTF16LE(option);
      let option_number_byte = stringToUint8ArrayUTF16LE(option_number);
      option_byte = [...option_byte, 13, 0];
      option_number_byte = [...option_number_byte, 13, 0];
      const option_char_cnt = intToByteLitEndian(option_byte.length / 2, 4);
      const option_number_char_cnt = intToByteLitEndian(
        option_number_byte.length / 2,
        4,
      );

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          i + 2,
          0,
          1,
          0,
          1,
          0,
          ...number_width,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0,
          0, 0,
        ],
      });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          i + 2,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(OPTION_NUMBER_WIDTH, 4),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_number_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_number_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: option_number_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          2,
          0,
          i + 2,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(
            table_width_ori - NUMBER_WIDTH - OPTION_NUMBER_WIDTH,
            4,
          ),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      if (option_byte.length / 2 > 1) {
        items.push({ tagId: 67, headerLevel: 3, data: option_byte });
      }
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [
          0,
          0,
          0,
          0,
          paperTab === PAPER_TABS.ANSWER_TEST_PAPER && text.answer === i + 1
            ? fontSize[2]
            : fontSize[0],
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });
    }

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        6,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// K타입 텍스트 항목 생성
const createKtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  examKorShow,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(
    `${[
      ...text.sentence.split(' ').map(text => {
        let isAnswerText = false;
        if (text.includes('{') && text.includes('}')) {
          isAnswerText = true;
        }
        let resultText = '';
        if (isAnswerText) {
          let filter_text = text.substring(text.indexOf('}') + 1);
          resultText = `___________________${filter_text} `;
        } else {
          resultText = `${text} `;
        }
        return resultText;
      }),
    ].join('')}`,
  );
  let answer_byte = stringToUint8ArrayUTF16LE(
    `${String.fromCharCode(9312 + text?.answer - 1)}`,
  );
  let translation_byte = stringToUint8ArrayUTF16LE(
    `${!examKorShow ? '' : `(${text?.translation ?? ''})`}`,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);
  translation_byte = new Uint8Array([...translation_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);
  const translation_char_cnt = intToByteLitEndian(
    translation_byte.length / 2,
    4,
  );

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(7, 2);
  const table_col = intToByteLitEndian(3, 2);

  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        2,
        0,
        2,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        2,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 83, 2, 0, 0, 212, 0, 0,
        0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        2,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        254,
        1,
        254,
        1,
        141,
        0,
        141,
        0,
        1,
        0,
        148,
        92,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    translation_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...translation_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (translation_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: translation_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, examKorShow ? fontSize[0] : 21, 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 83, 2, 0, 0, 212, 0, 0,
        0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    for (let i = 0; i < text.options.length; i++) {
      const option_number = `${String.fromCharCode(9312 + i)}`;
      const option = text.options[i];
      let option_byte = stringToUint8ArrayUTF16LE(option);
      let option_number_byte = stringToUint8ArrayUTF16LE(option_number);
      option_byte = [...option_byte, 13, 0];
      option_number_byte = [...option_number_byte, 13, 0];
      const option_char_cnt = intToByteLitEndian(option_byte.length / 2, 4);
      const option_number_char_cnt = intToByteLitEndian(
        option_number_byte.length / 2,
        4,
      );

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          i + 2,
          0,
          1,
          0,
          1,
          0,
          ...number_width,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0,
          0, 0,
        ],
      });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          i + 2,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(OPTION_NUMBER_WIDTH, 4),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_number_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_number_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: option_number_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          2,
          0,
          i + 2,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(
            table_width_ori - NUMBER_WIDTH - OPTION_NUMBER_WIDTH,
            4,
          ),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      if (option_byte.length / 2 > 1) {
        items.push({ tagId: 67, headerLevel: 3, data: option_byte });
      }
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [
          0,
          0,
          0,
          0,
          paperTab === PAPER_TABS.ANSWER_TEST_PAPER && text.answer === i + 1
            ? fontSize[2]
            : fontSize[0],
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });
    }

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        6,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        141,
        0,
        141,
        0,
        1,
        0,
        156,
        99,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// L타입 텍스트 항목 생성
const createLtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(`${strParse(text.question)}`);
  let answer_byte = stringToUint8ArrayUTF16LE(
    `${String.fromCharCode(9312 + text?.answer - 1)}`,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(6, 2);
  const table_col = intToByteLitEndian(3, 2);

  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        2,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        2,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    for (let i = 0; i < text.options.length; i++) {
      const option_number = `${String.fromCharCode(9312 + i)}`;
      const option = text.options[i];
      let option_byte = stringToUint8ArrayUTF16LE(option);
      let option_number_byte = stringToUint8ArrayUTF16LE(option_number);
      option_byte = [...option_byte, 13, 0];
      option_number_byte = [...option_number_byte, 13, 0];
      const option_char_cnt = intToByteLitEndian(option_byte.length / 2, 4);
      const option_number_char_cnt = intToByteLitEndian(
        option_number_byte.length / 2,
        4,
      );

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...number_width,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0,
          0, 0,
        ],
      });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(OPTION_NUMBER_WIDTH, 4),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_number_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_number_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: option_number_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          2,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(
            table_width_ori - NUMBER_WIDTH - OPTION_NUMBER_WIDTH,
            4,
          ),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      if (option_byte.length / 2 > 1) {
        items.push({ tagId: 67, headerLevel: 3, data: option_byte });
      }
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [
          0,
          0,
          0,
          0,
          paperTab === PAPER_TABS.ANSWER_TEST_PAPER && text.answer === i + 1
            ? fontSize[2]
            : fontSize[0],
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });
    }

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        5,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// M타입 텍스트 항목 생성
const createMtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  columCount,
) => {
  let index_byte = stringToUint8ArrayUTF16LE(`${index}.`);
  let question_byte = stringToUint8ArrayUTF16LE(`${strParse(text.question)}`);
  let answer_byte = stringToUint8ArrayUTF16LE(
    `${String.fromCharCode(9312 + text?.answer - 1)}`,
  );

  index_byte = new Uint8Array([...index_byte, 13, 0]);
  question_byte = new Uint8Array([...question_byte, 13, 0]);
  answer_byte = new Uint8Array([...answer_byte, 13, 0]);

  const index_char_cnt = intToByteLitEndian(index_byte.length / 2, 4);
  const question_char_cnt = intToByteLitEndian(question_byte.length / 2, 4);
  const answer_char_cnt = intToByteLitEndian(answer_byte.length / 2, 4);

  const items = [];

  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const number_width = intToByteLitEndian(NUMBER_WIDTH, 4);

  const table_row = intToByteLitEndian(6, 2);
  const table_col = intToByteLitEndian(3, 2);

  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        27,
        1,
        2,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        3,
        0,
        1,
        0,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        141,
        0,
        141,
        0,
        1,
        0,
        8,
        7,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        2,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    question_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...question_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: question_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
      ],
    });

    for (let i = 0; i < text.options.length; i++) {
      const option_number = `${String.fromCharCode(9312 + i)}`;
      const option = text.options[i];
      let option_byte = stringToUint8ArrayUTF16LE(option);
      let option_number_byte = stringToUint8ArrayUTF16LE(option_number);
      option_byte = [...option_byte, 13, 0];
      option_number_byte = [...option_number_byte, 13, 0];
      const option_char_cnt = intToByteLitEndian(option_byte.length / 2, 4);
      const option_number_char_cnt = intToByteLitEndian(
        option_number_byte.length / 2,
        4,
      );

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...number_width,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0,
          0, 0,
        ],
      });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(OPTION_NUMBER_WIDTH, 4),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_number_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_number_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: option_number_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          2,
          0,
          i + 1,
          0,
          1,
          0,
          1,
          0,
          ...intToByteLitEndian(
            table_width_ori - NUMBER_WIDTH - OPTION_NUMBER_WIDTH,
            4,
          ),
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      option_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...option_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      if (option_byte.length / 2 > 1) {
        items.push({ tagId: 67, headerLevel: 3, data: option_byte });
      }
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [
          0,
          0,
          0,
          0,
          paperTab === PAPER_TABS.ANSWER_TEST_PAPER && text.answer === i + 1
            ? fontSize[2]
            : fontSize[0],
          0,
          0,
          0,
        ],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });
    }

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        5,
        0,
        3,
        0,
        1,
        0,
        ...table_width,
        ...intToByteLitEndian(lineSpacing, 4),
        0,
        0,
        0,
        0,
        144,
        0,
        144,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        1, 0, 0, 128, 0, 0, 0, 0, 10, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
        0,
      ],
    });
    items.push({ tagId: 68, headerLevel: 3, data: [0, 0, 0, 0, 2, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 244, 1, 0, 0, 169, 1, 0, 0, 152,
        0, 0, 0, 0, 0, 0, 0, 156, 99, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({
      tagId: 68,
      headerLevel: 1,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 119, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9, 0, 0, 0, 1, 0, 2, 0, 0, 0, 0, 0, 0, 0, 141, 0, 27, 1, 2, 0, 3, 0, 0,
        0,
      ],
    }); //row size 체크

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...number_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    index_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...index_char_cnt,
        0,
        0,
        0,
        0,
        10,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    items.push({ tagId: 67, headerLevel: 3, data: index_byte });
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212, 0,
        0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
      ],
    });

    items.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - NUMBER_WIDTH, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    answer_char_cnt[3] += 128;
    items.push({
      tagId: 66,
      headerLevel: 2,
      data: [
        ...answer_char_cnt,
        0,
        0,
        0,
        0,
        15,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    if (answer_byte.length / 2 > 1) {
      items.push({ tagId: 67, headerLevel: 3, data: answer_byte });
    }
    items.push({
      tagId: 68,
      headerLevel: 3,
      data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
    });
    items.push({
      tagId: 69,
      headerLevel: 3,
      data: [
        0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 228,
        255, 255, 255, 0, 0, 0, 0, 72, 46, 0, 0, 0, 0, 6, 0,
      ],
    });
  }
  return items;
};

// 문서 생성 메인 함수
export const createDocumentHWP = async (
  orgName,
  className,
  teacherName,
  paperName,
  columCount,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
  examKorShow,
  base64Img,
  generatedPaper,
  paperTab,
  commonPaperVocaList,
) => {
  const normalId = 15; //9pt
  const normalBoldId = 16; //9pt
  const normalBoldUnderlineId = 17; //9pt
  const smallId = 12; //7pt
  const smallBoldId = 13; //7pt
  const smallBoldUnderlineId = 14; //7pt
  const largeId = 18; //11pt
  const largeBoldId = 19; //11pt
  const largeBoldUnderlineId = 20; //11pt

  const fontSizeMap = {
    [PAPER_CONFIG.FONT_SIZE.SMALL]: [
      smallId,
      smallBoldId,
      smallBoldUnderlineId,
    ],
    [PAPER_CONFIG.FONT_SIZE.NORMAL]: [
      normalId,
      normalBoldId,
      normalBoldUnderlineId,
    ],
    [PAPER_CONFIG.FONT_SIZE.LARGE]: [
      largeId,
      largeBoldId,
      largeBoldUnderlineId,
    ],
  };

  // fontSize가 정의되지 않은 경우, 기본값으로 NORMAL을 사용
  let wordTextSize =
    fontSizeMap[fontSize] || fontSizeMap[PAPER_CONFIG.FONT_SIZE.NORMAL];
  // 줄간격
  let lineSpacingValue = 1000;
  if (lineSpacing === PAPER_CONFIG.LINE_SPACING.SMALL) {
    lineSpacingValue = 800; // 0.8pt
  } else if (lineSpacing === PAPER_CONFIG.LINE_SPACING.NORMAL) {
    lineSpacingValue = 1000; // 1.0pt
  } else if (lineSpacing === PAPER_CONFIG.LINE_SPACING.LARGE) {
    lineSpacingValue = 1150; // 1.15pt
  }

  // '시험지/정답만/정답시험지' 13개 유형별로 항목 생성
  const makedItems = () => {
    let items = [];
    generatedPaper.forEach(type => {
      // 유형문제

      if (paperTab !== PAPER_TABS.ANSWER) {
        items = [...items, ...createSubject(type.title, wordTextSize)];
      }
      // 답변부
      type.questions.forEach((voca, index) => {
        switch (type.type) {
          case 'A':
            items = [
              ...items,
              ...createAtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                columCount,
              ),
            ];
            break;
          case 'B':
            items = [
              ...items,
              ...createBtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
                columCount,
              ),
            ];
            break;
          case 'C':
            items = [
              ...items,
              ...createCtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
                columCount,
              ),
            ];
            break;
          case 'D':
            items = [
              ...items,
              ...createDtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
                columCount,
              ),
            ];
            break;
          case 'E':
            items = [
              ...items,
              ...createEtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
                columCount,
              ),
            ];
            break;
          case 'F':
            items = [
              ...items,
              ...createFtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
                columCount,
              ),
            ];
            break;
          case 'G':
            items = [
              ...items,
              ...createGtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
                columCount,
              ),
            ];
            break;
          case 'H':
            if (voca?.options?.length !== 4) return;
            items = [
              ...items,
              ...createHtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                columCount,
              ),
            ];
            break;
          case 'I':
            if (voca?.options?.length !== 4) return;
            items = [
              ...items,
              ...createItypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                columCount,
              ),
            ];
            break;
          case 'J':
            if (voca?.options?.length !== 4) return;
            items = [
              ...items,
              ...createJtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                examKorShow,
                columCount,
              ),
            ];
            break;
          case 'K':
            if (voca?.options?.length !== 4) return;
            items = [
              ...items,
              ...createKtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                examKorShow,
                columCount,
              ),
            ];
            break;
          case 'L':
            if (voca?.options?.length !== 4) return;
            items = [
              ...items,
              ...createLtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                columCount,
              ),
            ];
            break;
          case 'M':
            if (voca?.options?.length !== 4) return;
            items = [
              ...items,
              ...createMtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                columCount,
              ),
            ];
            break;
          default:
            break;
        }
      });
    });
    return items;
  };

  // '어휘리스트' 시험지 전 유형 공통
  const makedCommonItems = () => {
    const items = [];

    const table_width = intToByteLitEndian(
      columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH,
      4,
    );
    const table_row = intToByteLitEndian(commonPaperVocaList?.length, 2);
    const table_col = intToByteLitEndian(2, 2);

    items.push({
      tagId: 66,
      headerLevel: 0,
      data: [
        9, 0, 0, 0, 0, 8, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
      ],
    });
    items.push({
      tagId: 67,
      headerLevel: 1,
      data: [11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0, 0, 0, 11, 0, 13, 0],
    });
    items.push({ tagId: 68, headerLevel: 1, data: [0, 0, 0, 0, 12, 0, 0, 0] });
    items.push({
      tagId: 69,
      headerLevel: 1,
      data: [
        0, 0, 0, 0, 34, 217, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
      ],
    });
    items.push({
      tagId: 71,
      headerLevel: 1,
      data: [
        32,
        108,
        98,
        116,
        16,
        35,
        42,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        ...table_width,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        92,
        134,
        101,
        105,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
    const row_size = [];
    for (let i = 0; i < commonPaperVocaList?.length; i++) {
      row_size.push(2);
      row_size.push(0);
    }
    items.push({
      tagId: 77,
      headerLevel: 2,
      data: [
        9,
        0,
        0,
        0,
        ...table_row,
        ...table_col,
        0,
        0,
        141,
        0,
        141,
        0,
        141,
        0,
        27,
        1,
        ...row_size,
        3,
        0,
        0,
        0,
      ],
    }); //row size 체크

    for (let i = 0; i < commonPaperVocaList?.length; i++) {
      const index_byte = intToByteLitEndian(i, 2);
      const eng_byte = [
        ...stringToUint8ArrayUTF16LE(commonPaperVocaList[i].eng),
        13,
        0,
      ];
      const kor_byte = [
        ...stringToUint8ArrayUTF16LE(commonPaperVocaList[i].kor),
        13,
        0,
      ];

      const eng_char_cnt = intToByteLitEndian(eng_byte.length / 2, 4);
      const kor_char_cnt = intToByteLitEndian(kor_byte.length / 2, 4);
      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          ...index_byte,
          1,
          0,
          1,
          0,
          88,
          50,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          141,
          0,
          141,
          0,
          3,
          0,
          88,
          50,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      eng_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...eng_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: eng_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 8, 7, 0, 0, 0, 0, 6, 0,
        ],
      });

      items.push({
        tagId: 72,
        headerLevel: 2,
        data: [
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          1,
          0,
          ...index_byte,
          1,
          0,
          1,
          0,
          88,
          50,
          0,
          0,
          0,
          0,
          0,
          0,
          254,
          1,
          254,
          1,
          141,
          0,
          141,
          0,
          3,
          0,
          88,
          50,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      kor_char_cnt[3] += 128;
      items.push({
        tagId: 66,
        headerLevel: 2,
        data: [
          ...kor_char_cnt,
          0,
          0,
          0,
          0,
          10,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          1,
          0,
          0,
          0,
          0,
          0,
          0,
          0,
        ],
      });
      items.push({ tagId: 67, headerLevel: 3, data: kor_byte });
      items.push({
        tagId: 68,
        headerLevel: 3,
        data: [0, 0, 0, 0, fontSize[0], 0, 0, 0],
      });
      items.push({
        tagId: 69,
        headerLevel: 3,
        data: [
          0, 0, 0, 0, 0, 0, 0, 0, 188, 2, 0, 0, 188, 2, 0, 0, 83, 2, 0, 0, 212,
          0, 0, 0, 0, 0, 0, 0, 148, 92, 0, 0, 0, 0, 6, 0,
        ],
      });
    }

    return items;
  };

  let bin_background_data = new Uint8Array(bin_background);

  let docinfo_data_arr = [];
  docinfo_data_arr.push({
    tagId: 16,
    headerLevel: 0,
    data: [
      1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 17,
    headerLevel: 0,
    data: [
      2, 0, 0, 0, 2, 0, 0, 0, 2, 0, 0, 0, 2, 0, 0, 0, 2, 0, 0, 0, 2, 0, 0, 0, 2,
      0, 0, 0, 2, 0, 0, 0, 9, 0, 0, 0, 22, 0, 0, 0, 5, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 20, 0, 0, 0, 3, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 18,
    headerLevel: 1,
    data: [33, 0, 1, 0, 3, 0, 106, 0, 112, 0, 103, 0],
  });
  let extension = stringToUint8ArrayUTF16LE('jpg');

  docinfo_data_arr.push({
    tagId: 18,
    headerLevel: 1,
    data: [33, 0, 2, 0, 3, 0, ...extension],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      97, 5, 0, 209, 185, 64, 199, 32, 0, 224, 172, 21, 181, 2, 11, 5, 3, 2, 0,
      0, 2, 0, 4, 13, 0, 77, 0, 97, 0, 108, 0, 103, 0, 117, 0, 110, 0, 32, 0,
      71, 0, 111, 0, 116, 0, 104, 0, 105, 0, 99, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      98, 2, 0, 133, 186, 112, 200, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 8, 0, 77, 0,
      121, 0, 101, 0, 111, 0, 110, 0, 103, 0, 106, 0, 111, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      97, 5, 0, 209, 185, 64, 199, 32, 0, 224, 172, 21, 181, 2, 11, 5, 3, 2, 0,
      0, 2, 0, 4, 13, 0, 77, 0, 97, 0, 108, 0, 103, 0, 117, 0, 110, 0, 32, 0,
      71, 0, 111, 0, 116, 0, 104, 0, 105, 0, 99, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      98, 2, 0, 133, 186, 112, 200, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 8, 0, 77, 0,
      121, 0, 101, 0, 111, 0, 110, 0, 103, 0, 106, 0, 111, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      97, 5, 0, 209, 185, 64, 199, 32, 0, 224, 172, 21, 181, 2, 11, 5, 3, 2, 0,
      0, 2, 0, 4, 13, 0, 77, 0, 97, 0, 108, 0, 103, 0, 117, 0, 110, 0, 32, 0,
      71, 0, 111, 0, 116, 0, 104, 0, 105, 0, 99, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      98, 2, 0, 133, 186, 112, 200, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 8, 0, 77, 0,
      121, 0, 101, 0, 111, 0, 110, 0, 103, 0, 106, 0, 111, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      97, 5, 0, 209, 185, 64, 199, 32, 0, 224, 172, 21, 181, 2, 11, 5, 3, 2, 0,
      0, 2, 0, 4, 13, 0, 77, 0, 97, 0, 108, 0, 103, 0, 117, 0, 110, 0, 32, 0,
      71, 0, 111, 0, 116, 0, 104, 0, 105, 0, 99, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      98, 2, 0, 133, 186, 112, 200, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 8, 0, 77, 0,
      121, 0, 101, 0, 111, 0, 110, 0, 103, 0, 106, 0, 111, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      97, 5, 0, 209, 185, 64, 199, 32, 0, 224, 172, 21, 181, 2, 11, 5, 3, 2, 0,
      0, 2, 0, 4, 13, 0, 77, 0, 97, 0, 108, 0, 103, 0, 117, 0, 110, 0, 32, 0,
      71, 0, 111, 0, 116, 0, 104, 0, 105, 0, 99, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      98, 2, 0, 133, 186, 112, 200, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 8, 0, 77, 0,
      121, 0, 101, 0, 111, 0, 110, 0, 103, 0, 106, 0, 111, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      97, 5, 0, 209, 185, 64, 199, 32, 0, 224, 172, 21, 181, 2, 11, 5, 3, 2, 0,
      0, 2, 0, 4, 13, 0, 77, 0, 97, 0, 108, 0, 103, 0, 117, 0, 110, 0, 32, 0,
      71, 0, 111, 0, 116, 0, 104, 0, 105, 0, 99, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      98, 2, 0, 133, 186, 112, 200, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 8, 0, 77, 0,
      121, 0, 101, 0, 111, 0, 110, 0, 103, 0, 106, 0, 111, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      97, 5, 0, 209, 185, 64, 199, 32, 0, 224, 172, 21, 181, 2, 11, 5, 3, 2, 0,
      0, 2, 0, 4, 13, 0, 77, 0, 97, 0, 108, 0, 103, 0, 117, 0, 110, 0, 32, 0,
      71, 0, 111, 0, 116, 0, 104, 0, 105, 0, 99, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 19,
    headerLevel: 1,
    data: [
      98, 2, 0, 133, 186, 112, 200, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 8, 0, 77, 0,
      121, 0, 101, 0, 111, 0, 110, 0, 103, 0, 106, 0, 111, 0,
    ],
  });
  //border fill
  docinfo_data_arr.push({
    tagId: 20,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 20,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 255, 255, 255, 255, 0, 0, 0, 0, 255, 255,
      255, 255, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 20,
    headerLevel: 1,
    data: [
      0, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0, 0,
      0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 20,
    headerLevel: 1,
    data: [
      0, 4, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 0, 0,
      0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 20,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0,
      0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 20,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 217, 217, 217, 0, 0, 0, 0, 0, 255, 255,
      255, 255, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 20,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 1, 0, 0, 0, 0, 0, 2, 0, 0, 0, 6, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 20,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 1, 0, 0, 0, 0, 0, 2, 0, 0, 0, 15, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 20,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 230, 230, 230, 0, 0, 0, 0, 0, 255, 255,
      255, 255, 0, 0, 0, 0, 0,
    ],
  });
  //글자모양
  // 글자크기 : 232,3,0,0 > 1000
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 232, 3, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); // 10pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 232, 3, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 1, 0, 0, 0, 0, 0,
    ],
  }); // 10pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 244, 1, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); // 5pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 176, 4, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 1, 0, 0, 0, 0, 0,
    ],
  }); // 12pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 32, 3, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  8pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 132, 3, 0, 0, 2, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  9pt 볼드
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 232, 3, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 178, 178, 178, 0, 1, 0, 0, 0, 0, 0,
    ],
  }); // 10pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 232, 3, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); // 10pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 44, 1, 0, 0, 2, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 178, 178, 178, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  3pt 볼드
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 32, 3, 0, 0, 0, 0, 0, 0, 10, 10, 178, 178, 178, 0, 0, 0, 0, 0,
      255, 255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); // 8pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 132, 3, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  9pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 232, 3, 0, 0, 2, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 178, 178, 178, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); // 10pt 볼드
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 188, 2, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  7pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 188, 2, 0, 0, 2, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  7pt 볼드
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 188, 2, 0, 0, 6, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  7pt 밑줄
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 132, 3, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  9pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 132, 3, 0, 0, 2, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  9pt 볼드
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 132, 3, 0, 0, 6, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  9pt 밑줄
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 76, 4, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  11pt 일반
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 76, 4, 0, 0, 2, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  11pt 볼드
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 76, 4, 0, 0, 6, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  11pt 밑줄
  docinfo_data_arr.push({
    tagId: 21,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100,
      100, 0, 0, 0, 0, 0, 0, 0, 100, 100, 100, 100, 100, 100, 100, 0, 0, 0, 0,
      0, 0, 0, 100, 0, 0, 0, 0, 0, 0, 0, 10, 10, 0, 0, 0, 0, 0, 0, 0, 0, 255,
      255, 255, 255, 192, 192, 192, 0, 2, 0, 0, 0, 0, 0,
    ],
  }); //  1pt 일반
  docinfo_data_arr.push({
    tagId: 22,
    headerLevel: 1,
    data: [0, 0, 0, 0, 0, 0, 0, 0],
  });
  docinfo_data_arr.push({
    tagId: 22,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 32, 0, 0, 0, 128, 62, 0, 0, 0, 0, 0, 0, 192, 93, 0, 0, 0, 0,
      0, 0, 0, 125, 0, 0, 0, 0, 0, 0, 64, 156, 0, 0, 0, 0, 0, 0, 72, 176, 0, 0,
      2, 0, 0, 0, 128, 187, 0, 0, 0, 0, 0, 0, 192, 218, 0, 0, 0, 0, 0, 0, 0,
      250, 0, 0, 0, 0, 0, 0, 64, 25, 1, 0, 0, 0, 0, 0, 128, 56, 1, 0, 0, 0, 0,
      0, 192, 87, 1, 0, 0, 0, 0, 0, 144, 96, 1, 0, 1, 0, 0, 0, 0, 119, 1, 0, 0,
      0, 0, 0, 64, 150, 1, 0, 0, 0, 0, 0, 128, 181, 1, 0, 0, 0, 0, 0, 192, 212,
      1, 0, 0, 0, 0, 0, 0, 244, 1, 0, 0, 0, 0, 0, 64, 19, 2, 0, 0, 0, 0, 0, 128,
      50, 2, 0, 0, 0, 0, 0, 192, 81, 2, 0, 0, 0, 0, 0, 0, 113, 2, 0, 0, 0, 0, 0,
      64, 144, 2, 0, 0, 0, 0, 0, 128, 175, 2, 0, 0, 0, 0, 0, 192, 206, 2, 0, 0,
      0, 0, 0, 0, 238, 2, 0, 0, 0, 0, 0, 64, 13, 3, 0, 0, 0, 0, 0, 128, 44, 3,
      0, 0, 0, 0, 0, 192, 75, 3, 0, 0, 0, 0, 0, 0, 107, 3, 0, 0, 0, 0, 0, 64,
      138, 3, 0, 0, 0, 0, 0, 128, 169, 3, 0, 0, 0, 0, 0, 192, 200, 3, 0, 0, 0,
      0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 22,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 31, 0, 0, 0, 64, 31, 0, 0, 0, 0, 0, 0, 128, 62, 0, 0, 0, 0, 0,
      0, 192, 93, 0, 0, 0, 0, 0, 0, 0, 125, 0, 0, 0, 0, 0, 0, 64, 156, 0, 0, 0,
      0, 0, 0, 128, 187, 0, 0, 0, 0, 0, 0, 192, 218, 0, 0, 0, 0, 0, 0, 0, 250,
      0, 0, 0, 0, 0, 0, 64, 25, 1, 0, 0, 0, 0, 0, 192, 87, 1, 0, 1, 0, 0, 0,
      128, 96, 1, 0, 0, 0, 0, 0, 0, 119, 1, 0, 0, 0, 0, 0, 64, 150, 1, 0, 0, 0,
      0, 0, 128, 181, 1, 0, 0, 0, 0, 0, 192, 212, 1, 0, 0, 0, 0, 0, 0, 244, 1,
      0, 0, 0, 0, 0, 64, 19, 2, 0, 0, 0, 0, 0, 128, 50, 2, 0, 0, 0, 0, 0, 192,
      81, 2, 0, 0, 0, 0, 0, 0, 113, 2, 0, 0, 0, 0, 0, 64, 144, 2, 0, 0, 0, 0, 0,
      128, 175, 2, 0, 0, 0, 0, 0, 192, 206, 2, 0, 0, 0, 0, 0, 0, 238, 2, 0, 0,
      0, 0, 0, 64, 13, 3, 0, 0, 0, 0, 0, 128, 44, 3, 0, 0, 0, 0, 0, 192, 75, 3,
      0, 0, 0, 0, 0, 0, 107, 3, 0, 0, 0, 0, 0, 64, 138, 3, 0, 0, 0, 0, 0, 128,
      169, 3, 0, 0, 0, 0, 0, 192, 200, 3, 0, 0, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 22,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 2, 0, 0, 0, 72, 176, 0, 0, 2, 0, 0, 0, 146, 96, 1, 0, 1, 0, 0,
      0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 22,
    headerLevel: 1,
    data: [2, 0, 0, 0, 0, 0, 0, 0],
  });
  //문단모양
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      12, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 4, 0, 0, 0, 2, 0, 27, 1, 27, 1, 27, 1, 27, 1, 0, 0, 0, 0, 0,
      0, 0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      128, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      180, 0, 0, 0, 2, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 180, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      128, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      128, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 64, 6, 0, 0,
      130, 0, 0, 0, 3, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 107, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      8, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      128, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 64, 6, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 107, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      4, 0, 0, 0, 0, 0, 0, 0, 104, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 64, 6, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      4, 0, 0, 0, 0, 0, 0, 0, 104, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      4, 1, 0, 0, 0, 0, 0, 0, 104, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 64, 6, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      12, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 80, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      128, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 3, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 100, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      128, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 1, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 120, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 96, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      12, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      12, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 80, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      12, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 80, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 25,
    headerLevel: 1,
    data: [
      4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      130, 0, 0, 0, 1, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 130, 0, 0, 0,
    ],
  });
  //스타일
  docinfo_data_arr.push({
    tagId: 26,
    headerLevel: 1,
    data: [
      3, 0, 20, 188, 213, 208, 0, 174, 6, 0, 78, 0, 111, 0, 114, 0, 109, 0, 97,
      0, 108, 0, 0, 0, 18, 4, 10, 0, 7, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 26,
    headerLevel: 1,
    data: [
      9, 0, 77, 0, 115, 0, 111, 0, 72, 0, 101, 0, 97, 0, 100, 0, 101, 0, 114, 0,
      9, 0, 77, 0, 115, 0, 111, 0, 72, 0, 101, 0, 97, 0, 100, 0, 101, 0, 114, 0,
      0, 1, 18, 4, 4, 0, 6, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 26,
    headerLevel: 1,
    data: [
      5, 0, 77, 0, 83, 0, 20, 188, 213, 208, 0, 174, 9, 0, 77, 0, 115, 0, 111,
      0, 78, 0, 111, 0, 114, 0, 109, 0, 97, 0, 108, 0, 0, 2, 18, 4, 6, 0, 6, 0,
      0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 27,
    headerLevel: 0,
    data: [
      28, 2, 1, 0, 0, 0, 7, 2, 0, 128, 7, 2, 8, 0, 0, 0, 6, 64, 6, 0, 0, 0, 0,
      0, 14, 64, 6, 0, 0, 0, 0, 0, 10, 64, 6, 0, 0, 0, 0, 0, 31, 64, 7, 0, 100,
      0, 0, 0, 29, 64, 6, 0, 0, 0, 0, 0, 26, 64, 6, 0, 0, 0, 0, 0, 16, 64, 7, 0,
      0, 0, 0, 0, 32, 64, 7, 0, 100, 0, 0, 0,
    ],
  });
  docinfo_data_arr.push({
    tagId: 94,
    headerLevel: 0,
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  });
  docinfo_data_arr.push({ tagId: 30, headerLevel: 0, data: [0, 0, 0, 0] });
  docinfo_data_arr.push({
    tagId: 31,
    headerLevel: 1,
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  });
  docinfo_data_arr.push({
    tagId: 32,
    headerLevel: 1,
    data: [
      56, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0,
    ],
  });

  let section_data_arr = [];
  let header_data = new Uint8Array([
    2, 0, 100, 99, 101, 115, 0, 0, 0, 0, 0, 0, 0, 0, 2, 0, 2, 0, 100, 108, 111,
    99, 0, 0, 0, 0, 0, 0, 0, 0, 2, 0, 11, 0, 32, 108, 98, 116, 0, 0, 0, 0, 0, 0,
    0, 0, 11, 0, 16, 0, 116, 111, 111, 102, 0, 0, 0, 0, 0, 0, 0, 0, 16, 0, 13,
    0,
  ]);
  section_data_arr.push({
    tagId: 66,
    headerLevel: 0,
    data: [
      ...intToByteLitEndian(header_data.length / 2, 4),
      4,
      0,
      0,
      0,
      19,
      0,
      0,
      3,
      1,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  });

  section_data_arr.push({ tagId: 67, headerLevel: 1, data: header_data });
  section_data_arr.push({
    tagId: 68,
    headerLevel: 1,
    data: [0, 0, 0, 0, 10, 0, 0, 0],
  });
  section_data_arr.push({
    tagId: 69,
    headerLevel: 1,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 132, 3, 0, 0, 132, 3, 0, 0, 253, 2, 0, 0, 16, 1,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 0,
    ],
  });
  section_data_arr.push({
    tagId: 71,
    headerLevel: 1,
    data: [
      100, 99, 101, 115, 0, 0, 8, 0, 110, 4, 0, 0, 0, 0, 64, 31, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  section_data_arr.push({
    tagId: 73,
    headerLevel: 2,
    data: [
      136, 232, 0, 0, 216, 72, 1, 0, 18, 11, 0, 0, 18, 11, 0, 0, 137, 5, 0, 0,
      137, 5, 0, 0, 137, 5, 0, 0, 137, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  section_data_arr.push({
    tagId: 74,
    headerLevel: 2,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 255, 255, 255, 255, 84, 3, 56, 2, 28,
      1, 1, 1, 0, 0, 0, 0,
    ],
  });
  section_data_arr.push({
    tagId: 74,
    headerLevel: 2,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 96, 3, 64, 2, 0, 0, 0, 1,
      0, 0, 0, 0,
    ],
  });
  section_data_arr.push({
    tagId: 75,
    headerLevel: 2,
    data: [1, 0, 0, 0, 137, 5, 137, 5, 137, 5, 137, 5, 7, 0],
  });
  section_data_arr.push({
    tagId: 75,
    headerLevel: 2,
    data: [1, 0, 0, 0, 137, 5, 137, 5, 137, 5, 137, 5, 1, 0],
  });
  section_data_arr.push({
    tagId: 75,
    headerLevel: 2,
    data: [1, 0, 0, 0, 137, 5, 137, 5, 137, 5, 137, 5, 1, 0],
  });
  if (columCount === 2) {
    // 다단
    section_data_arr.push({
      tagId: 71,
      headerLevel: 1,
      data: [100, 108, 111, 99, 8, 16, 137, 5, 0, 0, 3, 1, 178, 178, 178, 0],
    });
  } else {
    section_data_arr.push({
      tagId: 71,
      headerLevel: 1,
      data: [100, 108, 111, 99, 4, 16, 0, 0, 0, 0, 3, 1, 178, 178, 178, 0],
    });
  }

  //헤더
  const table_width_ori =
    columCount === 2 ? MAX_DOUBLE_WIDTH : MAX_SINGLE_WIDTH;
  const table_width = intToByteLitEndian(table_width_ori, 4);
  const name_width = 8000;
  const name_width_byte = intToByteLitEndian(name_width, 4);
  let orgName_byte = stringToUint8ArrayUTF16LE(orgName);
  let orgName_img_height = intToByteLitEndian(4000, 4);

  if (base64Img !== null) {
    orgName_byte = stringToUint8ArrayUTF16LE('');
  }
  orgName_byte = new Uint8Array([...orgName_byte, 13, 0]);
  let info = `${className} • ${teacherName}`;
  if (info.length === 3) {
    info = ' ';
  }
  let info_byte = stringToUint8ArrayUTF16LE(info);
  info_byte = new Uint8Array([...info_byte, 13, 0]);
  if (paperName.length === 0) {
    paperName = ' ';
  }
  let paperName_byte = stringToUint8ArrayUTF16LE(paperName);
  paperName_byte = new Uint8Array([...paperName_byte, 13, 0]);

  const orgName_char_cnt = intToByteLitEndian(orgName_byte.length / 2, 4);
  section_data_arr.push({
    tagId: 71,
    headerLevel: 1,
    data: [
      32,
      108,
      98,
      116,
      16,
      34,
      42,
      8,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      ...table_width,
      0,
      0,
      0,
      0,
      5,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      84,
      134,
      101,
      105,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  });
  section_data_arr.push({
    tagId: 77,
    headerLevel: 2,
    data: [
      6, 0, 0, 0, 4, 0, 2, 0, 0, 0, 168, 1, 168, 1, 113, 0, 113, 0, 2, 0, 1, 0,
      1, 0, 1, 0, 3, 0, 0, 0,
    ],
  }); //table

  //${orgName} > 이미지가 없을 때 기관명 노출
  if (base64Img !== null) {
    section_data_arr.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        32,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - name_width, 4),
        ...orgName_img_height,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        8,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
  } else {
    section_data_arr.push({
      tagId: 72,
      headerLevel: 2,
      data: [
        1,
        0,
        0,
        0,
        32,
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        1,
        0,
        1,
        0,
        ...intToByteLitEndian(table_width_ori - name_width, 4),
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        2,
        0,
        2,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ],
    });
  }
  orgName_char_cnt[3] += 128;
  section_data_arr.push({
    tagId: 66,
    headerLevel: 2,
    data: [
      ...orgName_char_cnt,
      0,
      8,
      0,
      0,
      10,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  });
  if (orgName_byte.length / 2 > 1) {
    section_data_arr.push({ tagId: 67, headerLevel: 3, data: orgName_byte });
  }
  section_data_arr.push({
    tagId: 68,
    headerLevel: 3,
    data: [0, 0, 0, 0, 4, 0, 0, 0],
  });
  section_data_arr.push({
    tagId: 69,
    headerLevel: 3,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 83, 15, 0, 0, 83, 15, 0, 0, 7, 13, 0, 0, 240, 0,
      0, 0, 0, 0, 0, 0, 224, 21, 0, 0, 0, 0, 6, 0,
    ],
  });
  section_data_arr.push({
    tagId: 72,
    headerLevel: 2,
    data: [
      1,
      0,
      0,
      0,
      32,
      0,
      1,
      0,
      1,
      0,
      0,
      0,
      1,
      0,
      2,
      0,
      ...name_width_byte,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      6,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  });
  section_data_arr.push({
    tagId: 66,
    headerLevel: 2,
    data: [
      5, 0, 0, 128, 0, 0, 0, 0, 16, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  section_data_arr.push({
    tagId: 67,
    headerLevel: 3,
    data: [40, 0, 116, 199, 132, 185, 41, 0, 13, 0],
  }); // (이름)
  section_data_arr.push({
    tagId: 68,
    headerLevel: 3,
    data: [0, 0, 0, 0, 9, 0, 0, 0],
  });
  section_data_arr.push({
    tagId: 69,
    headerLevel: 3,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 32, 3, 0, 0, 32, 3, 0, 0, 168, 2, 0, 0, 240, 0, 0,
      0, 0, 0, 0, 0, 4, 32, 0, 0, 0, 0, 6, 0,
    ],
  });

  section_data_arr.push({
    tagId: 72,
    headerLevel: 2,
    data: [
      1,
      0,
      0,
      0,
      32,
      0,
      1,
      0,
      0,
      0,
      1,
      0,
      1,
      0,
      1,
      0,
      ...intToByteLitEndian(table_width_ori - name_width, 4),
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      2,
      0,
      2,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  }); //list_head
  const info_byte_length = intToByteLitEndian(info_byte.length / 2, 4);
  info_byte_length[3] += 128;
  section_data_arr.push({
    tagId: 66,
    headerLevel: 2,
    data: [
      ...info_byte_length,
      0,
      0,
      0,
      0,
      10,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  });
  section_data_arr.push({ tagId: 67, headerLevel: 3, data: info_byte });
  section_data_arr.push({
    tagId: 68,
    headerLevel: 3,
    data: [0, 0, 0, 0, 4, 0, 0, 0],
  });
  section_data_arr.push({
    tagId: 69,
    headerLevel: 3,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 32, 3, 0, 0, 32, 3, 0, 0, 168, 2, 0, 0, 240, 0, 0,
      0, 0, 0, 0, 0, 164, 155, 0, 0, 0, 0, 6, 0,
    ],
  });

  section_data_arr.push({
    tagId: 72,
    headerLevel: 2,
    data: [
      1,
      0,
      0,
      0,
      32,
      0,
      1,
      0,
      0,
      0,
      2,
      0,
      2,
      0,
      1,
      0,
      ...table_width,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      2,
      0,
      2,
      5,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  });
  const paperName_byte_length = intToByteLitEndian(
    paperName_byte.length / 2,
    4,
  );
  paperName_byte_length[3] += 128;
  section_data_arr.push({
    tagId: 66,
    headerLevel: 2,
    data: [
      ...paperName_byte_length,
      0,
      0,
      0,
      0,
      12,
      0,
      1,
      0,
      1,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  });
  section_data_arr.push({ tagId: 67, headerLevel: 3, data: paperName_byte });
  section_data_arr.push({
    tagId: 68,
    headerLevel: 3,
    data: [0, 0, 0, 0, 11, 0, 0, 0],
  });
  section_data_arr.push({
    tagId: 69,
    headerLevel: 3,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 232, 3, 0, 0, 232, 3, 0, 0, 82, 3, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 164, 155, 0, 0, 0, 0, 6, 0,
    ],
  });

  section_data_arr.push({
    tagId: 72,
    headerLevel: 2,
    data: [
      1,
      0,
      0,
      0,
      32,
      0,
      1,
      0,
      0,
      0,
      3,
      0,
      2,
      0,
      1,
      0,
      ...table_width,
      244,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
  });
  section_data_arr.push({
    tagId: 66,
    headerLevel: 2,
    data: [
      1, 0, 0, 128, 0, 0, 0, 0, 12, 0, 1, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  section_data_arr.push({
    tagId: 68,
    headerLevel: 3,
    data: [0, 0, 0, 0, 8, 0, 0, 0],
  });
  section_data_arr.push({
    tagId: 69,
    headerLevel: 3,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 44, 1, 0, 0, 44, 1, 0, 0, 255, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 140, 209, 0, 0, 0, 0, 6, 0,
    ],
  });

  //헤더 여기까지

  section_data_arr.push({
    tagId: 71,
    headerLevel: 1,
    data: [116, 111, 111, 102, 0, 0, 0, 0, 15, 0, 0, 0],
  });
  section_data_arr.push({
    tagId: 72,
    headerLevel: 2,
    data: [
      1, 0, 0, 0, 64, 0, 0, 0, 100, 210, 0, 0, 137, 5, 0, 0, 0, 0, 0, 0, 0, 0,
      0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  section_data_arr.push({
    tagId: 66,
    headerLevel: 2,
    data: [
      9, 0, 0, 128, 0, 0, 4, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0,
    ],
  });
  section_data_arr.push({
    tagId: 67,
    headerLevel: 3,
    data: [18, 0, 111, 110, 116, 97, 0, 0, 0, 0, 0, 0, 0, 0, 18, 0, 13, 0],
  });
  section_data_arr.push({
    tagId: 68,
    headerLevel: 3,
    data: [0, 0, 0, 0, 0, 0, 0, 0],
  });
  section_data_arr.push({
    tagId: 69,
    headerLevel: 3,
    data: [
      0, 0, 0, 0, 0, 0, 0, 0, 232, 3, 0, 0, 232, 3, 0, 0, 82, 3, 0, 0, 44, 1, 0,
      0, 0, 0, 0, 0, 100, 210, 0, 0, 0, 0, 6, 0,
    ],
  });
  section_data_arr.push({
    tagId: 71,
    headerLevel: 3,
    data: [111, 110, 116, 97, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
  });

  section_data_arr = [
    ...section_data_arr,
    ...(paperTab !== PAPER_TABS.VOCABULARY_LIST
      ? makedItems(generatedPaper)
      : makedCommonItems(commonPaperVocaList)),
  ];
  const data = getBaseHWP(base_hwp);

  const cfb = CFB.read(base_hwp, { type: 'base64' });
  //console.log(cfb);
  for (let i = 0; i < data['root_entry'].length; i++) {
    if (data['root_entry'][i]['name'] === 'Section0') {
      for (let j = section_data_arr.length - 1; j >= 0; j--) {
        if (
          section_data_arr[j]['tagId'] === 66 &&
          section_data_arr[j]['headerLevel'] === 0
        ) {
          section_data_arr[j]['data'][3] += 128;
          break;
        }
      }

      const sector_byte = assembleBinaryData(section_data_arr);
      const section0_new_data = compressBinary(sector_byte);
      const origin_length = intToByteLitEndian(sector_byte.length, 4);
      // CRC32 값 계산
      const section0CRC32 = CRC32.buf(sector_byte) >>> 0;

      // 4바이트 리틀엔디언 변환
      const crcBytes = intToByteLitEndian(section0CRC32, 4);
      //("crc origin:"+section0CRC32);
      //console.log("crc:"+crcBytes);

      const section_combine_data = [];
      section_combine_data.push(section0_new_data);
      section_combine_data.push(crcBytes);
      section_combine_data.push(origin_length);
      const section0_new_data_combine = mergeBuffers(section_combine_data);

      //console.log("section len:"+section0_new_data.length +" / "+section0_new_data_combine.length);
      data['root_entry'][i]['document_raw'] = section0_new_data_combine;
      data['root_entry'][i]['stream_size'] = section0_new_data_combine.length;

      // HWP 헤더의 CRC 위치 업데이트 (예제에서는 오프셋 20번 바이트에 저장한다고 가정)
      //data["header"]["raw"].set(crcBytes, 20);

      let section0 = CFB.find(cfb, 'Root Entry/BodyText/Section0');
      section0.content = section0_new_data_combine; // "Hello" 저장
    } else if (data['root_entry'][i]['name'] === 'DocInfo') {
      //console.log(data["root_entry"][i]["document_raw"].slice(data["root_entry"][i]["document_raw"].length-8,data["root_entry"][i]["document_raw"].length-4));
      //console.log(byteToIntLitEndian(data["root_entry"][i]["document_raw"].slice(data["root_entry"][i]["document_raw"].length-4,data["root_entry"][i]["document_raw"].length))+"/"+decompressBinary(data["root_entry"][i]["document_raw"]).length + "/"+data["root_entry"][i]["stream_size"]+"/"+data["root_entry"][i]["document_raw"].length);

      const docinfo_byte = assembleBinaryData(docinfo_data_arr);
      const docinfo_new_data = compressBinary(docinfo_byte);
      const origin_length = intToByteLitEndian(docinfo_byte.length, 4);

      // CRC32 값 계산
      const section0CRC32 = CRC32.buf(docinfo_byte) >>> 0;
      // 4바이트 리틀엔디언 변환
      const crcBytes = intToByteLitEndian(section0CRC32, 4);
      //console.log("crc origin:"+section0CRC32);
      //console.log("crc:"+crcBytes);
      //console.log("header:"+data["header"]["raw"]);

      const docinfo_combine_data = [];
      docinfo_combine_data.push(docinfo_new_data);
      docinfo_combine_data.push(crcBytes);
      docinfo_combine_data.push(origin_length);
      const docinfo_new_data_combine = mergeBuffers(docinfo_combine_data);

      data['root_entry'][i]['document_raw'] = docinfo_new_data_combine;
      data['root_entry'][i]['stream_size'] = docinfo_new_data_combine.length;

      let docinfo = CFB.find(cfb, 'Root Entry/DocInfo');
      docinfo.content = docinfo_new_data_combine; // 문자열 저장
    }
  }

  CFB.utils.cfb_add(cfb, 'Root Entry/BinData/BIN0001.jpg', bin_background_data);
  if (base64Img !== null) {
    //const logo = base64ToArrayBuffer(base64Img);
    const logo_byte = new Uint8Array(base64Img);
    CFB.utils.cfb_add(cfb, 'Root Entry/BinData/BIN0002.jpg', logo_byte);
  }

  // 기존 CFB 파일 로드
  const newCfb = CFB.utils.cfb_new(); // 새로운 빈 CFB 생성

  // 기존 스트림 목록 가져오기
  const streamList = cfb.FullPaths;
  console.log('기존 스트림 목록:', streamList);

  streamList.forEach(stream => {
    if (stream !== 'Root Entry/\u0001Sh33tJ5') {
      // 제외할 스트림을 필터링
      console.log('스트림:', stream);
      const data = CFB.find(cfb, stream);
      if (data) {
        CFB.utils.cfb_add(newCfb, stream, data.content); // 새로운 CFB에 추가
      }
    }
  });

  // 새로운 CFB 파일 저장
  const updatedBuffer = CFB.write(newCfb, { type: 'array' });
  console.log('새로운 CFB 생성 완료');

  /*const sheetJ5 = CFB.find(cfb, "Root Entry/\u0001Sh33tJ5");
    console.log("찾은 스트림:", sheetJ5);
    if (sheetJ5) {
        // 스트림 삭제
        CFB.utils.cfb_del(cfb, "Root Entry/\u0001Sh33tJ5");
        console.log(`"${"Root Entry/\u0001Sh33tJ5"}" 스트림이 삭제되었습니다.`);
        CFB.utils.cfb_gc(cfb);
        if (CFB.find(cfb, "Root Entry/\u0001Sh33tJ5")) {
            console.log(`삭제가 되지 않았습니다. 다시 시도합니다.`);
            CFB.utils.cfb_del(cfb, "Root Entry/\u0001Sh33tJ5");
        } else {
            console.log(`삭제 성공!`);
        }
    } else {
        console.log(`"${"Root Entry/\u0001Sh33tJ5"}" 스트림을 찾을 수 없습니다.`);
    }*/

  //const hwpBuffer = makeHWP(data);

  //const re_base = uint8ArrayToBase64(hwpBuffer);
  //const reopen = getBaseHWP(re_base);

  // 파일 다운로드
  //const updatedBuffer = CFB.write(cfb, { type: "array" });
  //console.log(cfb);
  console.log(newCfb);

  let blob = new Blob([updatedBuffer], { type: 'application/octet-stream' });

  return blob;
};
